import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { CheckSquare, Copy, Trash2, X } from 'lucide-react'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSnapshot } from 'valtio'
import { store } from '@/store'
const stopPropagation = (e) => {
  e.stopPropagation()
}
const BulkCompleteAction = ({ onBulkComplete, isAllDone }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="ghost" size="sm" onClick={onBulkComplete}>
        <CheckSquare className="h-4 w-4 sm:mr-2" />
        <span className="hidden sm:inline">{isAllDone ? 'Todo' : 'Done'}</span>
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <p>Toggle</p>
    </TooltipContent>
  </Tooltip>
)
const BulkDuplicateAction = ({ onBulkDuplicate }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="ghost" size="sm" onClick={onBulkDuplicate}>
        <Copy className="h-4 w-4 sm:mr-2" />
        <span className="hidden sm:inline">Duplicate</span>
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <p className="flex items-center gap-2">
        Duplicate
        <Kbd>⌘D</Kbd>
      </p>
    </TooltipContent>
  </Tooltip>
)
const BulkDeleteAction = ({ onBulkDelete }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="ghost" size="sm" onClick={onBulkDelete} className="text-destructive">
        <Trash2 className="h-4 w-4 sm:mr-2" />
        <span className="hidden sm:inline">Delete</span>
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <p className="flex items-center gap-2">
        Delete
        <Kbd>⌫</Kbd>
      </p>
    </TooltipContent>
  </Tooltip>
)
const BulkClearAction = ({ onClearSelection }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 rounded-full shrink-0"
        onClick={onClearSelection}
      >
        <X className="h-3 w-3" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <p className="flex items-center gap-2">
        Clear
        <Kbd>Esc</Kbd>
      </p>
    </TooltipContent>
  </Tooltip>
)
export function TaskBulkActions({
  selectedIds,
  todos,
  onBulkComplete,
  onBulkDuplicate,
  onBulkDelete,
  onClearSelection,
}) {
  const isAllDone = todos
    .filter((todo) => selectedIds.has(todo.id))
    .every((todo) => todo.status === 'done')
  const snap = useSnapshot(store)
  const isContextMenuOpen = snap.uiSettings.isContextMenuOpen
  return (
    <AnimatePresence>
      {selectedIds.size > 0 && !isContextMenuOpen && (
        <motion.div
          initial={{
            opacity: 0,
            y: 100,
            x: '-50%',
          }}
          animate={{
            opacity: 1,
            y: 0,
            x: '-50%',
          }}
          exit={{
            opacity: 0,
            y: 100,
            x: '-50%',
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
          role="toolbar"
          aria-label="Bulk tasks actions"
          className="fixed bottom-6 left-1/2 z-[60] bg-card border shadow-xl rounded-full px-4 sm:px-6 py-2 flex items-center gap-2 sm:gap-4 bulk-actions-bar max-w-[95vw] group/task-actions"
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
        >
          <span className="text-sm font-medium mr-2 whitespace-nowrap">
            {selectedIds.size} <span className="hidden sm:inline">selected</span>
          </span>
          <div className="h-4 w-[1px] bg-border" />
          <BulkCompleteAction onBulkComplete={onBulkComplete} isAllDone={isAllDone} />
          <BulkDuplicateAction onBulkDuplicate={onBulkDuplicate} />
          <BulkDeleteAction onBulkDelete={onBulkDelete} />
          <div className="h-4 w-[1px] bg-border" />
          <BulkClearAction onClearSelection={onClearSelection} />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
export function ListBulkActions({
  selectedCount,
  onBulkDuplicate,
  onBulkDelete,
  onClearSelection,
}) {
  const snap = useSnapshot(store)
  const isContextMenuOpen = snap.uiSettings.isContextMenuOpen
  return (
    <AnimatePresence>
      {selectedCount > 0 && !isContextMenuOpen && (
        <motion.div
          initial={{
            opacity: 0,
            y: 100,
            x: '-50%',
          }}
          animate={{
            opacity: 1,
            y: 0,
            x: '-50%',
          }}
          exit={{
            opacity: 0,
            y: 100,
            x: '-50%',
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
          role="toolbar"
          aria-label="Bulk lists actions"
          className="fixed bottom-6 left-1/2 z-[60] bg-card border shadow-xl rounded-full px-4 sm:px-6 py-2 flex items-center gap-2 sm:gap-4 max-w-[95vw]"
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
        >
          <span className="text-sm font-medium mr-2 whitespace-nowrap">
            {selectedCount} <span className="hidden sm:inline">lists selected</span>
          </span>
          <div className="h-4 w-[1px] bg-border" />
          <Button variant="ghost" size="sm" onClick={onBulkDuplicate}>
            <Copy className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Duplicate</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onBulkDelete} className="text-destructive">
            <Trash2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
          <div className="h-4 w-[1px] bg-border" />
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full shrink-0"
            onClick={onClearSelection}
          >
            <X className="h-3 w-3" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
